import { Box } from "@mui/system";
import backgroundImage from "../../assets/images/background-room-showcase.jpg";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import { ROOM_TYPES, setCreateBookingForm } from "./HomeSlice";
import BookingForm from "./components/BookingForm";
import {
  getAppointmentsWithinADay,
  getCalendars,
  getCustomKeys,
  getOrganisation,
} from "./HomeApi";
import { getCalendarOperatingHoursForADay } from "./utils/getCalendarOperatingHoursForADay";
import momentTz from "moment-timezone";
import {
  AvailabilitySlot,
  generateStartTimes,
} from "./utils/generateStartTimes";
import { removeClashingTimeSlots } from "./utils/removeClashedTimeslots";
import { compressToUnqiueTimeslots } from "./utils/compressToUniqueTimeslots";
import {
  ALLOW_ONLINE_BOOKING_KEYWORD,
  DATE_FORMAT,
  DATE_RANGE_KEYWORD,
  MINIMUM_SCHEDULING_NOTICE_KEYWORD,
  TIMEZONE,
} from "../../config/helper";
import { useHistory } from "react-router-dom";
import { getPublicHolidaysWithEves } from "./utils/getPublicHolidaysWithEve";

const Home = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const customKeys = useAppSelector((state) => state.home.customKeys);
  const customFields = useAppSelector((state) => state.home.customFields);
  const bookingForm = useAppSelector((state) => state.home.createBookingForm);
  const calendars = useAppSelector((state) => state.home.calendars);
  const appointments = useAppSelector((state) => state.home.appointments);
  const isLoadingSlots = useAppSelector((state) => state.home.isLoadingSlots);

  const timeslotsWithRemovedClashes = useAppSelector(
    (state) => state.home.timeslotsWithRemovedClashes
  );

  const [minimumNotice, setMinimumNotice] = useState(3);
  const [dateRange, setDateRange] = useState(7);

  const roomTypeIsAllowed = (roomType: string) => {
    const roomTypesAllowed = ROOM_TYPES.filter((room) => room.visible).map(
      ({ label }) => label as string
    );
    return roomTypesAllowed.includes(roomType);
  };

  const filteredCalendars = calendars.filter((calendar) => {
    const allowOnlineBookingCustomField = calendar.customFields.find(
      (customField) =>
        customField.customKey.name === ALLOW_ONLINE_BOOKING_KEYWORD
    );
    if (allowOnlineBookingCustomField) {
      // If online booking field is present, we follow this behaviour
      // - empty value means -- allowed
      // - filled value means -- user explicitly indicate whether to make calendar available for online booking
      if (allowOnlineBookingCustomField.value) {
        if (allowOnlineBookingCustomField.value === "Yes") {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  });

  // Room types display is affected by pax
  const calendarsThatCanFitPax = filteredCalendars.filter(
    ({ customFields }) => {
      const maxPaxCustomField = customFields.find(
        (customField) => customField.customKey.name === "Max Pax"
      );
      const roomTypeCustomField = customFields.find(
        (customField) => customField.customKey.name === "Room Type"
      );

      if (
        maxPaxCustomField &&
        maxPaxCustomField.value &&
        roomTypeCustomField?.value
        // roomTypeIsAllowed(roomTypeCustomField.value)
      ) {
        return bookingForm.pax <= parseInt(maxPaxCustomField.value);
      } else {
        return false;
      }
    }
  );

  const availableRoomTypes = Array.from(
    new Set(
      calendarsThatCanFitPax
        .map(({ customFields }) => {
          const roomTypeCustomField = customFields.find(
            (customField) => customField.customKey.name === "Room Type"
          );
          if (
            roomTypeCustomField &&
            roomTypeCustomField.value
            // roomTypeIsAllowed(roomTypeCustomField.value)
          ) {
            return roomTypeCustomField.value;
          } else {
            return "";
          }
        })
        .filter((roomType) => roomType !== "")
    )
  );

  // TODO: This is a temporary function to "shut down" the site to prevent booking on certain dates durin trial period
  const filterDate = (date: moment.Moment) => {
    // if ([0, 5, 6].includes(date.day())) return false; // Uncomment this if you want the booking portal to be accessible on Friday, Saturday and Sunday

    // Auto route to /maintenance on EVE of public holidays
    // const holidayWithEves = getPublicHolidaysWithEves();
    // const formattedHolidayWithEves = holidayWithEves.map((d) =>
    //   d.format(DATE_FORMAT)
    // );

    // const formattedDate = date.tz(TIMEZONE).format(DATE_FORMAT);

    // if (formattedHolidayWithEves.includes(formattedDate)) {
    //   return false;
    // }

    return true;
  };

  useEffect(() => {
    dispatch(getOrganisation());
    const allowAccess = filterDate(momentTz().tz(TIMEZONE));
    if (!allowAccess) history.push("/maintenance");
  }, []);

  useEffect(() => {
    // When outlet changed, update the calendars
    dispatch(getCalendars({ outlet: bookingForm.outlet }));
    dispatch(getCustomKeys({ outlet: bookingForm.outlet }));
  }, [bookingForm.outlet]);

  useEffect(() => {
    const minimumSchedulingNoticeCustomField = customFields.find(
      ({ customKey }) => MINIMUM_SCHEDULING_NOTICE_KEYWORD === customKey.name
    );
    if (minimumSchedulingNoticeCustomField?.value)
      setMinimumNotice(parseInt(minimumSchedulingNoticeCustomField?.value));
    const dateRangeCustomField = customFields.find(
      ({ customKey }) => DATE_RANGE_KEYWORD === customKey.name
    );
    if (dateRangeCustomField?.value)
      setDateRange(parseInt(dateRangeCustomField?.value));
  }, [customFields]);

  useEffect(() => {
    // When pax changed, just remove the room type
    dispatch(
      setCreateBookingForm({
        ...bookingForm,
        roomType: "",
      })
    );
  }, [bookingForm.pax]);

  // Block off some dates on calendar
  useEffect(() => {
    const minimumSchedulingNoticeCustomField = customFields.find(
      ({ customKey }) => MINIMUM_SCHEDULING_NOTICE_KEYWORD === customKey.name
    );
    if (minimumSchedulingNoticeCustomField?.value)
      setMinimumNotice(parseInt(minimumSchedulingNoticeCustomField?.value));
    const dateRangeCustomField = customFields.find(
      ({ customKey }) => DATE_RANGE_KEYWORD === customKey.name
    );
    if (dateRangeCustomField?.value)
      setDateRange(parseInt(dateRangeCustomField?.value));
  }, [customFields]);

  /**
   * Get the opening and closing hours for the form chosen date
   * @returns Return opening hours and closing hours
   */
  const getOpeningAndClosingHours = () => {
    if (bookingForm.date && filteredCalendars.length > 0) {
      try {
        const startDate = new Date(bookingForm.date.toISOString());
        startDate.setHours(0);
        startDate.setMinutes(0);

        // Get closing hours which normally spill over to next day
        const nextDayOpeningHours = new Set();
        filteredCalendars.forEach((calendar) => {
          calendar.openHours.forEach((openHour) => {
            if (
              openHour.day === (startDate.getDay() + 1) % 7 &&
              openHour.startTime === "00:00:00"
            ) {
              nextDayOpeningHours.add(openHour.endTime);
            }
          });
        });

        const [endHour, endMinute, endSeconds] = (
          Array.from(nextDayOpeningHours)[0] as string
        ).split(":");

        const endDate = new Date(bookingForm.date.toISOString());
        endDate.setDate(endDate.getDate() + 1);
        endDate.setHours(parseInt(endHour));
        endDate.setMinutes(parseInt(endMinute));

        return {
          startTime: startDate.toISOString(),
          endTime: endDate.toISOString(),
        };
      } catch (error) {
        console.log("Invalid date: skip getting closing and opening hours.");
      }
    }
  };

  useEffect(() => {
    // When date changed, fetch the appointments and compute the display start and end time
    if (bookingForm.date) {
      const openingClosingHours = getOpeningAndClosingHours();

      if (!openingClosingHours) {
        return;
      }

      dispatch(
        getAppointmentsWithinADay({
          outlet: bookingForm.outlet,
          startTime: {
            startBound: openingClosingHours.startTime,
            endBound: openingClosingHours.endTime,
          },
          endTime: {
            startBound: openingClosingHours.startTime,
            endBound: openingClosingHours.endTime,
          },
          calendarUuids: filteredCalendars.map(
            (calendar: any) => calendar.uuid
          ),
        })
      );
      // Set new end date
    }
  }, [bookingForm.outlet, bookingForm.date]);

  let timeslotsToDisplay: { [key: string]: AvailabilitySlot[] } = {};
  if (
    Boolean(bookingForm.date) &&
    Boolean(bookingForm.roomType) &&
    Boolean(bookingForm.pax)
  ) {
    try {
      const chosenDateString = bookingForm.date?.toISOString() as string;
      const chosenDate = momentTz(chosenDateString).tz("Asia/Singapore");
      const startDay = chosenDate.day();
      const endDay = (chosenDate.day() + 1) % 7;

      const currentDate = momentTz().tz("Asia/Singapore");
      const isTomorrowOrLater =
        chosenDate.isSame(currentDate.clone().add(1, "days"), "day") ||
        chosenDate.isAfter(currentDate);

      if (isTomorrowOrLater) {
        const calendarsOperatingHourForFullDay: AvailabilitySlot[] = [];

        calendarsThatCanFitPax.forEach((calendar) => {
          const calendarOperatingHour = getCalendarOperatingHoursForADay(
            chosenDateString,
            calendar
          );

          const maxPaxCustomField = calendar.customFields.find(
            (customField) => customField.customKey.name === "Max Pax"
          );
          const roomTypeCustomField = calendar.customFields.find(
            (customField) => customField.customKey.name === "Room Type"
          );

          calendar.openHours
            .filter((openHour) => [startDay, endDay].includes(openHour.day))
            .forEach((openHour) => {
              const dateCopyStart =
                momentTz(chosenDateString).tz("Asia/Singapore");
              const dateCopyEnd =
                momentTz(chosenDateString).tz("Asia/Singapore");
              const [startHour, startMinute, startSeconds] =
                openHour.startTime.split(":");
              const [endHour, endMinute, endSeconds] =
                openHour.endTime.split(":");

              dateCopyStart.hour(parseInt(startHour));
              dateCopyStart.minute(parseInt(startMinute));
              dateCopyEnd.hour(parseInt(endHour));
              dateCopyEnd.minute(parseInt(endMinute));

              if (openHour.day === endDay) {
                dateCopyStart.add(1, "day");
                dateCopyEnd.add(1, "day");
              }

              const calendarOperatingHourSingleUnit = {
                calendarUuid: calendar.uuid,
                calendarName: calendar.name,
                calendarOpeningTime: calendarOperatingHour?.startTime as string,
                calendarClosingTime: calendarOperatingHour?.endTime as string,
                startTime: dateCopyStart.toISOString(),
                endTime: dateCopyEnd.toISOString(),
                maxPax:
                  maxPaxCustomField && maxPaxCustomField.value
                    ? (maxPaxCustomField.value as string)
                    : "0",
                roomType:
                  roomTypeCustomField && roomTypeCustomField.value
                    ? (roomTypeCustomField.value as string)
                    : "unknown",
              };

              calendarsOperatingHourForFullDay.push(
                calendarOperatingHourSingleUnit
              );
            });
        });

        const generatedTimes = generateStartTimes(
          chosenDateString,
          calendarsOperatingHourForFullDay,
          bookingForm.duration,
          bookingForm.pax,
          bookingForm.roomType
        );

        // For the generatedTimes, it doesn't have a endTime yet.
        // We will add the chosen duration (in hours) to the startTime indicated in
        // the generatedTimes to get the endTime
        const generatedTimesWithEndtime = generatedTimes.map(
          (generatedTime) => {
            const endTime = momentTz(generatedTime.startTime)
              .tz("Asia/Singapore")
              .add(bookingForm.duration, "hour");

            return {
              ...generatedTime,
              endTime: endTime.toISOString(),
            };
          }
        );

        // From generatedTimes, remove any one that clashed
        const timeslotsWithRemovedClashes = removeClashingTimeSlots(
          generatedTimesWithEndtime,
          appointments
        );

        const uniqueTimeslotsWithRemovedClashes = compressToUnqiueTimeslots(
          timeslotsWithRemovedClashes
        );

        timeslotsToDisplay = uniqueTimeslotsWithRemovedClashes;
      }
    } catch (error) {
      console.log("Invalid date: skip fetching of appointments");
    }
  }

  return (
    <Box
      sx={{
        background: `lightgray url(${backgroundImage}) 50% / cover no-repeat`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      {/* <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 3,
          padding: 4,
          maxWidth: 400,
          mx: 2,
          textAlign: "center",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              backgroundColor: "warning.light",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            ⚠️
          </Box>
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
            }}
          >
            Scheduled Maintenance
          </h1>
          <p style={{ color: "#666", fontSize: "1rem", lineHeight: 1.6 }}>
            We're currently undergoing maintenance. For booking-related
            requests, please contact us via WhatsApp.
          </p>
        </Box>

        <a
          href="https://api.whatsapp.com/send/?phone=6589091123"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Box
            component="button"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              width: "100%",
              backgroundColor: "#25D366",
              color: "white",
              fontWeight: "bold",
              py: 1.5,
              px: 3,
              borderRadius: 1,
              border: "none",
              cursor: "pointer",
              fontSize: "1rem",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#128C7E",
                boxShadow: 2,
              },
            }}
          >
            <span style={{ fontSize: "1.25rem" }}>💬</span>
            WhatsApp Us: +65 8909 1123
          </Box>
        </a>
      </Box> */}

      <BookingForm
        customKeys={customKeys}
        form={bookingForm}
        availableRoomTypes={availableRoomTypes}
        availableTimeslots={timeslotsToDisplay}
        isLoadingTimeslots={isLoadingSlots}
        minimumNoticeInDays={minimumNotice}
        dateRangeInDays={dateRange}
      />
    </Box>
  );
};

export default Home;
